'use client';

import useLocalStorage from '@/hooks/useLocalStorage';
import { mdiClose } from '@mdi/js';
import {
	FC,
	ReactNode,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { twMerge } from 'tailwind-merge';
import {
	COLOR_BLINDNESS_SIMULATION_FILTERS,
	ColorBlindnessSimulationFilter,
} from '../ArticleAccessibilityMenu/ArticleAccessibilityMenu';
import IconButton from '../Button/IconButton';

export const useIsSimulationEnabled = () => {
	const [isBlurredVisionEnabled, setIsBlurredVisionEnabled] = useLocalStorage(
		'isBlurredVisionEnabled',
		false,
	);

	const [isColorBlindVisionEnabled, setIsColorBlindVisionEnabled] =
		useLocalStorage<ColorBlindnessSimulationFilter['id']>(
			'isColorBlindVisionEnabled',
			false,
		);

	const [isVisualFieldLossTubeEnabled, setIsVisualFieldLossTubeEnabled] =
		useLocalStorage('isVisualFieldLossTubeEnabled', false);

	const isAnySimulationEnabled = useMemo(
		() =>
			isBlurredVisionEnabled ||
			isColorBlindVisionEnabled ||
			isVisualFieldLossTubeEnabled,
		[
			isBlurredVisionEnabled,
			isColorBlindVisionEnabled,
			isVisualFieldLossTubeEnabled,
		],
	);

	const setIsAnySimulationEnabled = useCallback(
		(isEnabled: false) => {
			setIsBlurredVisionEnabled(isEnabled);
			setIsColorBlindVisionEnabled(isEnabled);
			setIsVisualFieldLossTubeEnabled(isEnabled);
		},
		[
			setIsBlurredVisionEnabled,
			setIsColorBlindVisionEnabled,
			setIsVisualFieldLossTubeEnabled,
		],
	);

	return {
		isBlurredVisionEnabled,
		setIsBlurredVisionEnabled,
		isColorBlindVisionEnabled,
		setIsColorBlindVisionEnabled,
		isVisualFieldLossTubeEnabled,
		setIsVisualFieldLossTubeEnabled,
		isAnySimulationEnabled,
		setIsAnySimulationEnabled,
	};
};

export const SimulationLayout: FC<{
	children: ReactNode;
}> = ({ children }) => {
	const elementRef = useRef<HTMLDivElement>(null);
	const blurredVisionElementRef = useRef<HTMLDivElement>(null);
	const colorBlindVisionElementRef = useRef<HTMLDivElement>(null);

	const [hasRendered, setHasRendered] = useState(false);

	useEffect(() => {
		setHasRendered(typeof window !== 'undefined');
	}, []);

	const {
		isBlurredVisionEnabled,
		isColorBlindVisionEnabled,
		isVisualFieldLossTubeEnabled,
	} = useIsSimulationEnabled();
	useEffect(() => {
		// Ensure the component has rendered and the element is available
		if (!hasRendered) return;

		const element = blurredVisionElementRef.current;
		if (!element) return;

		if (isBlurredVisionEnabled)
			element.classList.add('simulation-blurred-vision');
		else element.classList.remove('simulation-blurred-vision');
	}, [hasRendered, isBlurredVisionEnabled]);
	useEffect(() => {
		// Ensure the component has rendered and the element is available
		if (!hasRendered) return;

		const element = colorBlindVisionElementRef.current;
		if (!element) return;

		COLOR_BLINDNESS_SIMULATION_FILTERS.forEach((filter) => {
			element.classList.remove(`simulation-colorblindness-${filter.id}`);
		});
		if (typeof isColorBlindVisionEnabled === 'string') {
			element.classList.add(
				`simulation-colorblindness-${isColorBlindVisionEnabled}`,
			);
		}
	}, [hasRendered, isColorBlindVisionEnabled]);

	const visualFieldLossTubeRef = useRef<HTMLDivElement>(null);

	const onMouseMove = useCallback((event: MouseEvent) => {
		// Get the correct mouse position
		const rect = elementRef.current?.getBoundingClientRect();
		const mousePosition = rect
			? {
					x: event.clientX - rect.left,
					y: event.clientY - rect.top,
				}
			: null;

		// Move the tube with the cursor
		if (visualFieldLossTubeRef.current && mousePosition) {
			visualFieldLossTubeRef.current.style.opacity = '1';
			visualFieldLossTubeRef.current.style.left = `${mousePosition.x}px`;
			visualFieldLossTubeRef.current.style.top = `${mousePosition.y}px`;
		}
	}, []);

	useEffect(() => {
		document.addEventListener('mousemove', onMouseMove);
		return () => document.removeEventListener('mousemove', onMouseMove);
	}, [isVisualFieldLossTubeEnabled, onMouseMove]);

	return (
		<>
			<ActiveSimulationBar />
			<div ref={elementRef}>
				<div ref={blurredVisionElementRef}>
					<div ref={colorBlindVisionElementRef}>
						<div
							className={twMerge(
								hasRendered &&
									isVisualFieldLossTubeEnabled &&
									'relative overflow-hidden rounded-2xl',
							)}>
							{hasRendered && isVisualFieldLossTubeEnabled && (
								<div
									ref={visualFieldLossTubeRef}
									className="pointer-events-none absolute left-1/2 top-1/2 z-200 size-48 -translate-x-1/2 -translate-y-1/2 rounded-full opacity-0 blur-lg transition-opacity duration-500"
									style={{
										boxShadow: '0 0 0 800vw #000',
									}}
								/>
							)}
							{children}
						</div>
					</div>
				</div>
			</div>
		</>
	);
};

const ActiveSimulationBar: FC = () => {
	const [hasRendered, setHasRendered] = useState(false);

	useEffect(() => {
		setHasRendered(typeof window !== 'undefined');
	}, []);

	const { isAnySimulationEnabled, setIsAnySimulationEnabled } =
		useIsSimulationEnabled();

	if (!hasRendered || !isAnySimulationEnabled) return null;

	return (
		<nav
			aria-label="Actieve simulaties"
			className="relative z-10 bg-primary-500 p-2 shadow-lg md:px-6 dark:bg-primary-950">
			<div className="mx-auto flex w-full max-w-5xl gap-4">
				<div className="w-full shrink grow" />
				<IconButton
					icon={mdiClose}
					label="Stop alle simulaties"
					title="Stop alle simulaties"
					className="shrink-0"
					onClick={() => setIsAnySimulationEnabled(false)}
				/>
			</div>
		</nav>
	);
};
